import type { Part, Section } from '@/lib/types'

/**
 * Listening media resolution (RUNNER-CONTRACT §3.1, §3.5).
 *
 * The audio source for a part is the FIRST `media_resources` entry whose
 * `media_type` is `audio`. In preview mode every part resolves to a bundled
 * local fixture instead - preview never touches live media and never creates
 * MediaResource records; the fixture is a self-contained data URI so no
 * network request of any kind is issued.
 */

function uint32(value: number): string {
  return String.fromCharCode(value & 0xff, (value >> 8) & 0xff, (value >> 16) & 0xff, (value >> 24) & 0xff)
}

const SAMPLE_RATE = 8000

/**
 * A valid WAV (8kHz, 8-bit, mono) carrying an actual tone, as a data URI.
 *
 * This must be AUDIBLE. A silent fixture loads, plays and emits `ended`
 * exactly the same way, but a human previewing the sample journal then cannot
 * tell working playback from broken playback - the transport says "playing"
 * and nothing comes out. The tone alternates between two pitches so that
 * "audio is running" is unmistakable by ear.
 */
function buildTonePreviewWav(seconds: number): string {
  const sampleCount = Math.round(SAMPLE_RATE * seconds)
  let samples = ''
  for (let i = 0; i < sampleCount; i += 1) {
    const t = i / SAMPLE_RATE
    const hz = Math.floor(t * 2) % 2 === 0 ? 440 : 587
    // Gentle amplitude, and a short fade at each end so playback does not click.
    const edge = Math.min(1, i / 400, (sampleCount - i) / 400)
    samples += String.fromCharCode(128 + Math.round(46 * edge * Math.sin(2 * Math.PI * hz * t)))
  }
  const header =
    'RIFF' + uint32(36 + sampleCount) + 'WAVE'
    + 'fmt ' + uint32(16)
    + String.fromCharCode(1, 0, 1, 0) // PCM, mono
    + uint32(SAMPLE_RATE) + uint32(SAMPLE_RATE) // sample rate, byte rate
    + String.fromCharCode(1, 0, 8, 0) // block align, 8-bit samples
    + 'data' + uint32(sampleCount)
  return btoa(header + samples)
}

/**
 * The bundled sample-journal audio: self-contained, so preview issues no
 * network request of any kind and creates no MediaResource records.
 */
export const PREVIEW_AUDIO_SRC = `data:audio/wav;base64,${buildTonePreviewWav(4)}`

export function firstAudioResource(part: Part): { id: string; url: string; durationSeconds?: number } | null {
  const resource = (part.media_resources ?? []).find((entry) => entry.media_type === 'audio')
  if (!resource || typeof resource.file_path !== 'string' || !resource.file_path) return null
  return {
    id: String(resource.id),
    url: resource.file_path,
    durationSeconds: typeof resource.duration_seconds === 'number' ? resource.duration_seconds : undefined,
  }
}

/**
 * Resolves a part's playable audio URL.
 *
 * Authored audio wins in every mode, including preview: admin preview exists to
 * rehearse a real paper, and an examiner who cannot hear the recording cannot
 * review it. Playback is a READ - it creates no Progress, Result or
 * MediaResource record - so it does not breach preview's no-write contract.
 * The bundled fixture is the fallback for preview when a part has no resolvable
 * audio, which is what the local sample journal relies on.
 */
export function resolvePartAudioUrl(part: Part, preview: boolean): string | null {
  const authored = firstAudioResource(part)?.url ?? null
  if (authored) return authored
  return preview ? PREVIEW_AUDIO_SRC : null
}

/**
 * The durable same-origin media URL for a resource - the re-resolution target
 * when a 24-hour signed `file_path` has expired (§3.1). A read endpoint:
 * playback authenticates with the session cookie and creates no records.
 */
export function durableMediaUrl(resourceId: string): string {
  const base = import.meta.env.VITE_API_BASE_URL || '/mocktest'
  return `${base}/media/${resourceId}/`
}

/** The signed-URL fallback for a part's audio; preview needs none. */
export function resolvePartAudioFallbackUrl(part: Part, preview: boolean): string | null {
  if (preview) return null
  const resource = firstAudioResource(part)
  return resource ? durableMediaUrl(resource.id) : null
}

/**
 * True when the section's Listening time is derived from summed audio
 * durations - the condition under which the 2-minute final answer check
 * applies (§3.5). Mirrors the domain mapper's duration source rule.
 */
export function hasAudioDerivedDuration(section: Section): boolean {
  if (section.type !== 'Listening') return false
  const total = (section.parts ?? []).reduce((sum, part) => {
    const audio = (part.media_resources ?? []).filter((resource) => resource.media_type === 'audio')
    return sum + audio.reduce((partSum, resource) => {
      const seconds = Number(resource.duration_seconds)
      return partSum + (Number.isFinite(seconds) && seconds > 0 ? seconds : 0)
    }, 0)
  }, 0)
  return total > 0
}
